import { ActivityTypes } from '../Model/Activity';

import {
  WorkoutPublishStatus,
  isLongSessionWorkout,
  isWorkoutPublished,
} from './longSession';

const ExerciseTagCategory = {
  EXERCISE_TYPE: 'EXERCISE_TYPE',
  EQUIPMENT: 'EQUIPMENT',
  BODY_REGION_BASIC: 'BODY_REGION_BASIC',
  BODY_REGION_ADVANCED: 'BODY_REGION_ADVANCED',
};

const WorkoutAssignmentCode = {
  NO_ACTIVITIES_IN_WORKOUT: 'This workout doesn\'t contain any activities',
  NO_ACTIVITIES_IN_CIRCUIT: 'One of the circuits in the workout doesn\'t contain any activities',
  ONLY_REST_ACTIVITIES: 'This workout only has REST activities',
  ONLY_REST_ACTIVITIES_IN_CIRCUIT: 'One of the circuits in the workout only has REST activities',
  VALID_WORKOUT: 'Valid workout',
  LONG_SESSION_TOO_MANY_ACTIVITIES: 'A long session holds exactly one video and nothing else',
  LONG_SESSION_WRONG_ACTIVITY: 'A long session can only contain its own session video',
  LONG_SESSION_VIDEO_MISSING: 'This session\'s video is not ready to play yet',
  LONG_SESSION_ACTIVITY_IN_STANDARD_WORKOUT: 'A session video cannot be part of a standard workout',
  WORKOUT_NOT_READY: 'This workout is still being prepared and can\'t be assigned yet',
  WORKOUT_FAILED: 'This workout\'s video didn\'t finish processing. Open it to retry.',
};

/* TODO: Add Activity model and types.
* This will be necessary for Workouts 2.0.
*/

const COMPARE_REST = 'REST';

const getRequiredEquipment = (activities = []) => {
  let equipment = {};

  activities.forEach((activity) => {
    if (activity.activities) {
      const circuitEquipment = getRequiredEquipment(activity.activities);
      equipment = {
        ...equipment,
        ...circuitEquipment,
      };
    } else {
      const { tags = [] } = activity;
      const equipmentTags = tags.filter((tag) => tag.category === ExerciseTagCategory.EQUIPMENT);
      equipmentTags.forEach(({ id, tag }) => {
        equipment[id] = tag;
      });
    }
  });

  return equipment;
};

const validateActivities = (activitiesArr, isRootArr) => {
  if (activitiesArr.activities && activitiesArr.activities.length > 0) {
    let onlyRest = true;
    let innerCircuitCode = '';

    // Function to verify if workout has valid activities array
    const validArray = activitiesArr.activities.every((act) => {
      if (onlyRest && act.type && act.type !== COMPARE_REST) {
        onlyRest = false;
      }

      if (act.activities) {
        innerCircuitCode = validateActivities(act, false);
        if (innerCircuitCode !== true) {
          return false;
        }
      }
      return true;
    });

    if (isRootArr) {
      if (onlyRest) {
        return WorkoutAssignmentCode.ONLY_REST_ACTIVITIES; // Only REST activities in root array
      }
      if (innerCircuitCode !== true) {
        return innerCircuitCode; // No activities or only REST activities in nested array
      }
      return WorkoutAssignmentCode.VALID_WORKOUT; // Workout is valid
    }

    if (!isRootArr && onlyRest) {
      return WorkoutAssignmentCode.ONLY_REST_ACTIVITIES_IN_CIRCUIT;
    }

    return validArray;
  }
  if (!isRootArr && activitiesArr.activities && activitiesArr.activities.length === 0) {
    return WorkoutAssignmentCode.NO_ACTIVITIES_IN_CIRCUIT; // No activities in nested array
  }
  return WorkoutAssignmentCode.NO_ACTIVITIES_IN_WORKOUT; // No activities in root array
};

/**
 * Whether a LONG_SESSION activity appears anywhere in an activity tree. A standard workout must not
 * contain one: the two session types are mutually exclusive, and the mobile executor has no
 * processor for a session video.
 *
 * @param {Array} activities
 * @returns {boolean}
 */
const hasLongSessionActivity = (activities = []) => activities.some((activity) => (
  activity.type === ActivityTypes.LONG_SESSION
  || hasLongSessionActivity(activity.activities)
));

/**
 * Validates a workout's content against its session type.
 *
 * Unlike {@link validateActivities}, which only ever sees an activity array, this reads the workout
 * as a whole — which is the only way to answer "is this content valid *for this kind of workout*".
 * It is called at the builder's single save boundary, shared by create, update, fork and assignment
 * update. The assignment pickers reach it through {@link validateWorkoutForAssignment}, which refuses
 * a workout that is not READY before checking its content here.
 *
 * A PENDING long session legitimately has no activity yet: that is its normal state while the video
 * is uploading and being processed. The activity is written by the server once the video is READY.
 *
 * @param {Object} workoutContent
 * @param {string} [workoutContent.sessionType] Absent means STANDARD.
 * @param {string} [workoutContent.publishStatus] Absent means READY.
 * @param {Array} [workoutContent.activities]
 * @returns {string} One of {@link WorkoutAssignmentCode}; VALID_WORKOUT when the content is valid.
 */
const validateWorkoutContent = ({ sessionType, publishStatus, activities = [] } = {}) => {
  // Absent means READY, as the field is documented everywhere else; read it once so both checks agree.
  const status = publishStatus || WorkoutPublishStatus.READY;

  if (!isLongSessionWorkout({ sessionType })) {
    if (hasLongSessionActivity(activities)) {
      return WorkoutAssignmentCode.LONG_SESSION_ACTIVITY_IN_STANDARD_WORKOUT;
    }
    return validateActivities({ activities }, true);
  }

  // The video is still in flight; there is nothing to validate yet and nothing to assign either.
  if (!activities.length) {
    return status === WorkoutPublishStatus.READY
      ? WorkoutAssignmentCode.LONG_SESSION_VIDEO_MISSING
      : WorkoutAssignmentCode.VALID_WORKOUT;
  }

  if (activities.length > 1) {
    return WorkoutAssignmentCode.LONG_SESSION_TOO_MANY_ACTIVITIES;
  }

  const [activity] = activities;
  if (activity.type !== ActivityTypes.LONG_SESSION || activity.activities) {
    return WorkoutAssignmentCode.LONG_SESSION_WRONG_ACTIVITY;
  }

  // Only a READY session is claiming to be playable, so only a READY session must prove it.
  if (status === WorkoutPublishStatus.READY && !activity.video?.hlsVideoUrl) {
    return WorkoutAssignmentCode.LONG_SESSION_VIDEO_MISSING;
  }

  return WorkoutAssignmentCode.VALID_WORKOUT;
};

/**
 * Validates a workout for *assignment*, as opposed to for saving.
 *
 * The difference is one rule, and it matters: {@link validateWorkoutContent} deliberately accepts a
 * PENDING long session with no activity yet, because that is its normal state while the video is in
 * flight and the coach must still be able to save its name and note. Assigning one would hand a
 * client a workout with no video, so assignability is checked first and separately.
 *
 * This is the affordance, not the boundary — the server refuses a non-READY workout at every path
 * that creates an assignment. Its job is to stop offering a workout the server would reject, so the
 * coach never picks one only to be told no.
 *
 * Absent `publishStatus` means READY, so every workout that predates long sessions is unaffected.
 *
 * @param {Object} workoutContent
 * @returns {string} One of {@link WorkoutAssignmentCode}; VALID_WORKOUT when the workout is assignable.
 */
const validateWorkoutForAssignment = (workoutContent = {}) => {
  if (!isWorkoutPublished(workoutContent)) {
    return workoutContent.publishStatus === WorkoutPublishStatus.FAILED
      ? WorkoutAssignmentCode.WORKOUT_FAILED
      : WorkoutAssignmentCode.WORKOUT_NOT_READY;
  }

  return validateWorkoutContent(workoutContent);
};

export {
  getRequiredEquipment,
  WorkoutAssignmentCode,
  validateActivities,
  validateWorkoutContent,
  validateWorkoutForAssignment,
};
